"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.dictionaryApi = exports.projectsApi = void 0;
const axios_1 = __importDefault(require("axios"));
// Use relative URL for API calls - nginx proxy will handle routing to backend
// Ensure we use the same protocol as the current page (HTTPS in production)
const API_BASE_URL = '/api/v1';
// CRITICAL: Create axios clients WITHOUT baseURL to prevent absolute URL creation
const apiClient = axios_1.default.create({
    // NO baseURL - will use relative URLs only
    timeout: 60000, // Zvýšeno na 60 sekund pro základní operace
    headers: {
        'Content-Type': 'application/json',
    },
});
// Speciální klient pro dlouhé operace (stahování, transkripce, AI korekce)
const longOperationClient = axios_1.default.create({
    // NO baseURL - will use relative URLs only
    timeout: 300000, // 5 minut pro dlouhé operace
    headers: {
        'Content-Type': 'application/json',
    },
});
// Společné interceptory pro oba klienty
const setupInterceptors = (client) => {
    // Request interceptor for error handling and protocol enforcement
    client.interceptors.request.use((config) => {
        // Debug logging
        console.log('Axios request config BEFORE processing:', {
            url: config.url,
            baseURL: config.baseURL,
            windowProtocol: window.location.protocol,
            windowHost: window.location.host,
            windowOrigin: window.location.origin
        });
        // CRITICAL FIX: Ensure baseURL is always relative
        if (config.baseURL && config.baseURL.startsWith('http')) {
            console.warn('Found absolute baseURL, converting to relative:', config.baseURL);
            // Extract path from absolute URL
            try {
                const url = new URL(config.baseURL);
                config.baseURL = url.pathname;
                console.warn('Converted baseURL to relative:', config.baseURL);
            }
            catch (e) {
                console.error('Failed to parse baseURL:', e);
                config.baseURL = '/api/v1'; // Fallback
            }
        }
        // CRITICAL FIX: Ensure URL is always relative
        if (config.url && config.url.startsWith('http')) {
            console.warn('Found absolute URL, converting to relative:', config.url);
            try {
                const url = new URL(config.url);
                config.url = url.pathname + url.search + url.hash;
                console.warn('Converted URL to relative:', config.url);
            }
            catch (e) {
                console.error('Failed to parse URL:', e);
                // Keep original URL as fallback
            }
        }
        // Final debug logging
        console.log('Axios request config AFTER processing:', {
            url: config.url,
            baseURL: config.baseURL,
            finalURL: config.baseURL && config.url ? config.baseURL + config.url : config.url
        });
        return config;
    }, (error) => Promise.reject(error));
    // Response interceptor for error handling
    client.interceptors.response.use((response) => response, (error) => {
        console.error('API Error:', error.response?.data || error.message);
        return Promise.reject(error);
    });
};
setupInterceptors(apiClient);
setupInterceptors(longOperationClient);
exports.projectsApi = {
    async createProject(youtubeUrl) {
        const response = await apiClient.post('/api/v1/projects/', {
            youtube_url: youtubeUrl,
        });
        return response.data;
    },
    async getProjects() {
        const response = await apiClient.get('/api/v1/projects/');
        return response.data;
    },
    async getProject(projectId) {
        const response = await apiClient.get(`/api/v1/projects/${projectId}`);
        return response.data;
    },
    async getProjectSources(projectId) {
        // Mock implementation - replace with actual API call
        // const response = await apiClient.get(`/api/v1/projects/${projectId}/sources`);
        // return response.data;
        // Mock data for development - only return sources that actually exist
        const project = await this.getProject(projectId);
        const availableSources = [];
        // Check if project has segments and determine available sources based on project status
        if (project.segments && project.segments.length > 0) {
            // Only add Whisper source if project has been processed (has segments)
            if (project.status === 'needs_review' || project.status === 'completed') {
                availableSources.push({
                    source_id: 'whisper-source-1',
                    source_type: 'whisper_transcription',
                    source_name: 'Whisper AI transkripce',
                    language: 'cs',
                    total_segments: project.segments.length,
                    status: 'completed',
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                });
            }
            // Only add YouTube source if:
            // 1. Video URL exists and contains youtube
            // 2. Project has been processed (indicating subtitles were downloaded)
            if ((project.video_url || project.youtube_url) &&
                (project.video_url?.includes('youtube') || project.youtube_url?.includes('youtube')) &&
                (project.status === 'needs_review' || project.status === 'completed')) {
                // For now, only add if we have actual YouTube subtitles
                // This would be determined by checking if subtitles were actually downloaded
                // For demo purposes, add it for completed projects
                availableSources.push({
                    source_id: 'youtube-source-1',
                    source_type: 'youtube_manual',
                    source_name: 'YouTube manuální titulky',
                    language: 'cs',
                    total_segments: project.segments.length,
                    status: 'completed',
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                });
            }
        }
        return availableSources;
    },
    async updateSegment(projectId, segmentId, correctedText) {
        const response = await apiClient.put(`/api/v1/projects/${projectId}/segments/${segmentId}`, { corrected_text: correctedText });
        return response.data;
    },
    async applySuggestion(projectId, segmentId, suggestionId) {
        await apiClient.post(`/api/v1/projects/${projectId}/segments/${segmentId}/suggestions/${suggestionId}/apply`);
    },
    async rejectSuggestion(projectId, segmentId, suggestionId) {
        await apiClient.post(`/api/v1/projects/${projectId}/segments/${segmentId}/suggestions/${suggestionId}/reject`);
    },
    async getExportPreview(projectId) {
        const response = await apiClient.get(`/api/v1/projects/${projectId}/export/preview`);
        return response.data;
    },
    async exportProject(projectId) {
        const response = await apiClient.post(`/api/v1/projects/${projectId}/export`, {}, { responseType: 'blob' });
        return response.data;
    },
    async getExportOptions(projectId) {
        const response = await apiClient.get(`/api/v1/projects/${projectId}/export-options`);
        return response.data;
    },
    async exportAdvanced(projectId, options) {
        const config = options.return_content ? {} : { responseType: 'blob' };
        const response = await apiClient.post(`/api/v1/projects/${projectId}/export-advanced`, options, config);
        return response.data;
    },
    // Nové API metody pro manuální akce
    async getAvailableSubtitles(projectId) {
        const response = await apiClient.get(`/api/v1/projects/${projectId}/available-subtitles`);
        return response.data;
    },
    async extractSubtitles(projectId, language = 'cs') {
        const response = await apiClient.post(`/api/v1/projects/${projectId}/extract-subtitles?language=${language}`);
        return response.data;
    },
    async downloadAudio(projectId) {
        const response = await longOperationClient.post(`/api/v1/projects/${projectId}/download-audio`);
        return response.data;
    },
    async transcribeWithWhisper(projectId) {
        const response = await longOperationClient.post(`/api/v1/projects/${projectId}/transcribe-whisper`);
        return response.data;
    },
    async correctWithAI(projectId) {
        const response = await longOperationClient.post(`/api/v1/projects/${projectId}/correct-ai`);
        return response.data;
    },
    async resetProject(projectId) {
        const response = await apiClient.post(`/api/v1/projects/${projectId}/reset`);
        return response.data;
    },
    async deleteProject(projectId) {
        await apiClient.delete(`/api/v1/projects/${projectId}`);
    },
    // === NOVÉ API PRO SPRÁVU ZDROJŮ TITULKŮ ===
    async getProjectSubtitleSources(projectId) {
        const response = await apiClient.get(`/api/v1/projects/${projectId}/sources`);
        return response.data;
    },
    async createYouTubeSubtitleSource(projectId, language = 'cs') {
        const response = await longOperationClient.post(`/api/v1/projects/${projectId}/sources/youtube`, null, {
            params: { language }
        });
        return response.data;
    },
    async createWhisperSubtitleSource(projectId, audioFilePath) {
        const response = await longOperationClient.post(`/api/v1/projects/${projectId}/sources/whisper`, {
            audio_file_path: audioFilePath
        });
        return response.data;
    },
    async getSourceSegments(projectId, sourceId) {
        const response = await apiClient.get(`/api/v1/projects/${projectId}/sources/${sourceId}/segments`);
        return response.data;
    },
    async deleteSubtitleSource(projectId, sourceId) {
        const response = await apiClient.delete(`/api/v1/projects/${projectId}/sources/${sourceId}`);
        return response.data;
    },
    async getSourcesComparison(projectId) {
        const response = await apiClient.get(`/api/v1/projects/${projectId}/sources/comparison`);
        return response.data;
    },
};
exports.dictionaryApi = {
    async getTerms() {
        const response = await apiClient.get('/api/v1/dictionary/');
        return response.data;
    },
    async addTerm(phrase, caseSensitive) {
        const response = await apiClient.post('/api/v1/dictionary/', {
            phrase,
            case_sensitive: caseSensitive,
        });
        return response.data;
    },
    async deleteTerm(termId) {
        await apiClient.delete(`/api/v1/dictionary/${termId}`);
    },
};
exports.default = apiClient;
